/**
 * Currency formatting — one formatter for the whole app, in the signed-in
 * account's own currency and locale (from /auth/me: home_currency + locale).
 * A South African account reads R 12 345,67 exactly as before; an account in
 * Zimbabwe on USD reads US$12,345.67. App.jsx calls configureMoney() with the
 * account before anything renders. Prices Vault charges (Paystack, in rand)
 * use fmtZAR, never the account currency. Accepts numbers or the API's decimal
 * strings; bad input renders as zero.
 *
 * Also the ONE per-row money function the whole product derives from. Pass 3
 * decision (the ledger + detail card): every header total must be computed from
 * the SAME function as the rows beneath it, so a header can never say
 * "R 1 612,00 of R 3 600,00" while the only visible row says "R 0,00 spent".
 * `unitRate` and `spentOf` used to be redefined per file (Dashboard, DetailCard,
 * SpendMap) with subtly different rules — the exact way a header and its rows
 * drift apart. Import these instead of writing new ones.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
// The account's money style. Starts South African so a page that renders before
// /auth/me answers (the public site, the splash) reads exactly as it always did.
let money = { currency: 'ZAR', locale: 'en-ZA' };

// Build a formatter, falling back to rand if the browser rejects a code.
function makeFormat(currency, locale, digits) {
  const opts = { style: 'currency', currency };
  if (digits != null) { opts.minimumFractionDigits = digits; opts.maximumFractionDigits = digits; }
  try { return new Intl.NumberFormat(locale, opts); } catch { /* unknown code or locale */ }
  try { return new Intl.NumberFormat('en-ZA', { ...opts, currency: 'ZAR' }); } catch { return null; }
}

let nf = makeFormat('ZAR', 'en-ZA', 2);
let nfWhole = makeFormat('ZAR', 'en-ZA', 0);
const zarNf = makeFormat('ZAR', 'en-ZA', 2);

/** Point every formatter at the account's currency and locale (idempotent). */
export function configureMoney(me) {
  const currency = String(me?.home_currency || 'ZAR').toUpperCase();
  const locale = String(me?.locale || 'en-ZA');
  if (currency === money.currency && locale === money.locale) return;
  money = { currency, locale };
  // Most currencies have 2 minor units; JPY 0, BHD 3. Intl knows, so ask it.
  const probe = makeFormat(currency, locale);
  const minor = probe ? probe.resolvedOptions().maximumFractionDigits : 2;
  nf = makeFormat(currency, locale, minor);
  nfWhole = makeFormat(currency, locale, 0);
}

/** The account's ISO 4217 code and locale, for screens that need them. */
export const moneyCurrency = () => money.currency;
export const moneyLocale = () => money.locale;

const fmtWith = (f, v) => (f ? f.format(Number(v) || 0) : String(Number(v) || 0));

/** An amount in the account's currency, with its minor units: R 12 345,67. */
export const fmtR = (v) => fmtWith(nf, v);
/** The same, rounded to whole units for tight spaces: R 12 346. */
export const fmtWhole = (v) => fmtWith(nfWhole, v);
/** Prices Vault itself charges, always in rand whatever the account's currency. */
export const fmtZAR = (v) => fmtWith(zarNf, v);

/** Just the currency symbol in the account's style, e.g. "R", "US$", "€". */
export function moneySymbol() {
  try {
    const part = nf.formatToParts(0).find((x) => x.type === 'currency');
    return part ? part.value : money.currency;
  } catch { return money.currency; }
}

/**
 * Read an amount someone typed, whichever decimal mark their keyboard uses:
 * "12,50", "12.50", "1 234,56", "1,234.56" and "1.234,56" all work. The LAST
 * comma or dot followed by one or two digits is the decimal mark; every other
 * separator is grouping. Returns NaN for text with no number in it.
 */
export function parseAmount(text) {
  let s = String(text ?? '').trim().replace(/[\s\u00a0\u202f']/g, '').replace(/[^0-9,.\-]/g, '');
  if (!/[0-9]/.test(s)) return NaN;
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  const m = /[.,](\d{1,2})$/.exec(s);
  let whole = s, frac = '';
  if (m) { whole = s.slice(0, m.index); frac = m[1]; }
  whole = whole.replace(/[.,]/g, '');
  const n = Number(`${whole || '0'}${frac ? `.${frac}` : ''}`);
  return neg ? -n : n;
}

/**
 * The unit price behind a "rate × count" formula — the LARGER factor of the
 * FIRST `rate*count` term ONLY, so 200*31 and 31*200 both rate at R200, and
 * 600*2+1700 rates R600 (two R600 units plus a one-off R1700), NOT R1700 — a
 * trailing flat term must never hijack the rate. A flat amount (no product
 * term) rates 0. This mirrors the server's `unit_rate` byte-for-byte so a row's
 * spend and every header derived from it read the same on client and server.
 */
export function unitRate(calc) {
  const term = String(calc || '').replace(/×/g, '*').split('+').find((t) => t.includes('*'));
  if (!term) return 0;
  const nums = (term.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  return nums.length ? Math.max(...nums) : 0;
}

/**
 * What an expense has actually cost so far — the single source of truth the
 * card header, the list rows and every section total all read:
 *   spent = actual (Σ money visits) + reduce_n × unit rate
 * A set total (`spent_override`, #314) IS the spend and replaces both, exactly
 * as the server's `_expense_spent` and the phone read it; leaving it out made a
 * row disagree with its bank card after Set total (#772).
 * A completed (ticked) item reports its settled `actual` (else its budget):
 * the tick folds money logged by hand and units into actual and records how many
 * units it counted (`tick_units`), so only units logged after the tick add on
 * top. Rows ticked before `tick_units` existed keep the larger of the two.
 * This mirrors the server's `_expense_spent` exactly. Non-expenses have no spend.
 */
export function spentOf(item) {
  if (!item || item.kind !== 'expense') return 0;
  if (item.spent_override != null && item.spent_override !== '') return Number(item.spent_override);
  const rate = unitRate(item.calculation);
  const n = Number(item.reduce_n || 0);
  if (item.completed) {
    const settled = (item.actual != null && item.actual !== '') ? Number(item.actual) : Number(item.amount || 0);
    if (item.tick_units != null) return settled + rate * Math.max(0, n - Number(item.tick_units));
    return Math.max(settled, rate * n);
  }
  return Number(item.actual || 0) + (n > 0 ? n * rate : 0);
}

/**
 * Still owed on an expense — never negative. `leftOf` = budget − spent, floored
 * at 0 (overspend shows as spent > budget, not negative "left").
 */
export function leftOf(item) {
  return Math.max(0, (Number(item?.amount) || 0) - spentOf(item));
}

/**
 * Where an open expense's spend came from, so a row can show BOTH halves (#772):
 * money logged by hand (`actual`) and formula units (count × unit price). A set
 * total replaces both and is reported on its own. Figures are rands.
 */
export function spendParts(item) {
  if (!item || item.kind !== 'expense') return { override: null, logged: 0, units: 0, unitsTotal: 0 };
  if (item.spent_override != null && item.spent_override !== '') {
    return { override: Number(item.spent_override), logged: 0, units: 0, unitsTotal: 0 };
  }
  const units = Number(item.reduce_n || 0);
  return {
    override: null,
    logged: Number(item.actual || 0),
    units,
    unitsTotal: units > 0 ? units * unitRate(item.calculation) : 0,
  };
}

/**
 * One line naming where the spend came from — "R 150,00 logged + 2 units R 400,00",
 * "2 units R 400,00", "set total" — or '' when nothing is logged. Only open items
 * get it: a ticked item's actual already holds its settled total.
 */
export function spendBreakdown(item) {
  if (!item || item.kind !== 'expense' || item.completed) return '';
  const p = spendParts(item);
  if (p.override != null) return 'set total';
  const bits = [];
  if (p.logged > 0) bits.push(`${fmtR(p.logged)} logged`);
  if (p.units > 0) bits.push(`${p.units} ${p.units === 1 ? 'unit' : 'units'} ${fmtR(p.unitsTotal)}`);
  return bits.join(' + ');
}

/** Sum of `spentOf` across a set of items — the ONE way to build a header total. */
export const sumSpent = (items) => (items || []).reduce((a, i) => a + spentOf(i), 0);
/** Sum of budgeted amounts across expenses — the header's "of R X". */
export const sumBudget = (items) => (items || []).reduce((a, i) => a + (Number(i.amount) || 0), 0);
